import React, { useState, useEffect } from "react";
import {
  X,
  Folder,
  File,
  ChevronRight,
  Search,
  Upload,
  CheckCircle,
} from "lucide-react";
import useEdrive from "../../hooks/useEdrive";

/**
 * EDrive 파일 선택 모달
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 닫기 핸들러
 * @param {function} onSelect - 파일 선택 핸들러
 * @param {boolean} multiple - 다중 선택 허용 여부
 */
export default function FileSelectorModal({
  isOpen,
  onClose,
  onSelect,
  multiple = true,
}) {
  const edrive = useEdrive();
  const [currentPath, setCurrentPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);

  // 루트 폴더 로드
  useEffect(() => {
    if (isOpen && currentPath.length === 0) {
      loadRootFolders();
    }
  }, [isOpen]);

  const loadRootFolders = async () => {
    setLoading(true);
    const result = await edrive.fetchRootFolders();
    if (result.ok) {
      setFolders(result.folders);
      setFiles([]);
    }
    setLoading(false);
  };

  const loadSubFolder = async (folderId, folderName) => {
    setLoading(true);
    const result = await edrive.fetchSubFolder(folderId);
    if (result.ok) {
      setFolders(result.folders || []);
      setFiles(result.files || []);
      setCurrentPath([...currentPath, { id: folderId, name: folderName }]);
    }
    setLoading(false);
  };

  const navigateToPath = (index) => {
    if (index === -1) {
      setCurrentPath([]);
      loadRootFolders();
    } else {
      const newPath = currentPath.slice(0, index + 1);
      const lastFolder = newPath[newPath.length - 1];
      setCurrentPath(newPath.slice(0, -1));
      loadSubFolder(lastFolder.id, lastFolder.name);
    }
  };

  const handleFileSelect = (file) => {
    if (multiple) {
      setSelectedFiles((prev) => {
        const exists = prev.some((f) => f.id === file.id);
        if (exists) {
          return prev.filter((f) => f.id !== file.id);
        }
        return [...prev, file];
      });
    } else {
      setSelectedFiles([file]);
    }
  };

  const isFileSelected = (fileId) => {
    return selectedFiles.some((f) => f.id === fileId);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setLoading(true);
    const result = await edrive.searchFiles(searchQuery);
    if (result.ok) {
      setSearchResults(result.result);
    }
    setLoading(false);
  };

  const handleFileUpload = async (event) => {
    const fileList = Array.from(event.target.files);
    if (fileList.length === 0) return;

    setLoading(true);
    const currentFolderId =
      currentPath.length > 0 ? currentPath[currentPath.length - 1].id : "";

    const result = await edrive.uploadFiles(fileList, currentFolderId);

    if (result.ok) {
      // 업로드 후 현재 폴더 새로고침
      if (currentFolderId) {
        const folder = currentPath[currentPath.length - 1];
        setCurrentPath(currentPath.slice(0, -1));
        await loadSubFolder(folder.id, folder.name);
      } else {
        await loadRootFolders();
      }
    }

    setLoading(false);
    setUploadMode(false);
  };

  const handleConfirm = () => {
    onSelect(selectedFiles);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">파일 선택</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 검색 바 */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="파일 검색..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              검색
            </button>
            <button
              onClick={() => setUploadMode(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              <Upload className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 경로 표시 */}
        <div className="px-4 py-2 border-b bg-gray-50">
          <div className="flex items-center text-sm">
            <button
              onClick={() => navigateToPath(-1)}
              className="text-blue-500 hover:text-blue-600"
            >
              홈
            </button>
            {currentPath.map((path, index) => (
              <React.Fragment key={path.id}>
                <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
                <button
                  onClick={() => navigateToPath(index)}
                  className="text-blue-500 hover:text-blue-600"
                >
                  {path.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* 파일 목록 */}
        <div className="flex-1 p-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">로딩 중...</div>
            </div>
          ) : searchResults ? (
            // 검색 결과
            <div>
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  검색 결과 ({searchResults.total_files}개 파일,{" "}
                  {searchResults.total_folders}개 폴더)
                </h4>
                <button
                  onClick={() => {
                    setSearchResults(null);
                    setSearchQuery("");
                  }}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  검색 취소
                </button>
              </div>

              {/* 검색된 폴더 */}
              {searchResults.folders.map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => loadSubFolder(folder.id, folder.folder_name)}
                >
                  <Folder className="w-5 h-5 text-yellow-500 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{folder.folder_name}</div>
                    <div className="text-sm text-gray-500">{folder.path}</div>
                  </div>
                </div>
              ))}

              {/* 검색된 파일 */}
              {searchResults.files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer ${
                    isFileSelected(file.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={() => handleFileSelect(file)}
                >
                  <File className="w-5 h-5 text-gray-500 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{file.file_name}</div>
                    <div className="text-sm text-gray-500">
                      {(file.file_size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  {isFileSelected(file.id) && (
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            // 일반 폴더/파일 목록
            <div>
              {/* 폴더 목록 */}
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => loadSubFolder(folder.id, folder.folder_name)}
                >
                  <Folder className="w-5 h-5 text-yellow-500 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{folder.folder_name}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(folder.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}

              {/* 파일 목록 */}
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer ${
                    isFileSelected(file.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={() => handleFileSelect(file)}
                >
                  <File className="w-5 h-5 text-gray-500 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{file.file_name}</div>
                    <div className="text-sm text-gray-500">
                      {(file.file_size / 1024 / 1024).toFixed(2)} MB •
                      {new Date(file.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  {isFileSelected(file.id) && (
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                  )}
                </div>
              ))}

              {folders.length === 0 && files.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  폴더가 비어있습니다
                </div>
              )}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedFiles.length}개 파일 선택됨
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedFiles.length === 0}
                className={`px-4 py-2 rounded-lg ${
                  selectedFiles.length === 0
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                선택 완료
              </button>
            </div>
          </div>
        </div>

        {/* 업로드 모달 */}
        {uploadMode && (
          <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center">
            <div className="text-center">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex flex-col items-center"
              >
                <Upload className="w-16 h-16 text-gray-400 mb-4" />
                <span className="text-lg font-medium mb-2">
                  파일을 선택하세요
                </span>
                <span className="text-sm text-gray-500">
                  또는 여기에 드래그하세요
                </span>
              </label>
              <button
                onClick={() => setUploadMode(false)}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
